import { useState } from 'react'

export interface UseCoverImageFieldProps {
  maxFileSize: number

  validationErrorRequiredField: string
  validationErrorInvalidFileType: string
  validationErrorFileTooLarge: string
}

export interface UseCoverImageFieldResult {
  coverImage: File | undefined
  coverImageError: string | undefined
  setCoverImageError: (error: string | undefined) => void
  handleCoverImageChange: (
    file: File | string,
    fileName?: string
  ) => Promise<void>
}

const urlToFile = async (
  imageUrl: string,
  imageName?: string
): Promise<File> => {
  const response = await fetch(imageUrl)
  const blob = await response.blob()

  return new File([blob], imageName || 'img', { type: blob.type })
}

const checkIfImageRenders = ({
  file,
  handleError,
}: Readonly<{
  file: File
  handleError: VoidFunction
}>) => {
  const reader = new FileReader()

  reader.onload = (event) => {
    const result = event.target?.result

    if (typeof result !== 'string') {
      handleError()

      return
    }

    const img = new Image()

    img.onerror = () => {
      handleError()
    }

    img.src = result
  }

  reader.onerror = () => {
    handleError()
  }

  reader.readAsDataURL(file)
}

export const useCoverImageField = ({
  maxFileSize,

  validationErrorRequiredField,
  validationErrorInvalidFileType,
  validationErrorFileTooLarge,
}: UseCoverImageFieldProps): UseCoverImageFieldResult => {
  const [coverImage, setCoverImage] = useState<File | undefined>(undefined)
  const [coverImageError, setCoverImageError] = useState<string | undefined>(
    undefined
  )

  const handleCoverImageChange = async (
    file: File | string,
    fileName?: string
  ) => {
    if (!file) {
      setCoverImageError(validationErrorRequiredField)

      return
    }

    setCoverImageError(undefined)

    if (typeof file === 'string') {
      const imageFile = await urlToFile(file, `${fileName || 'cover'}.jpeg`)

      checkIfImageRenders({
        file: imageFile,
        handleError: () => setCoverImageError(validationErrorInvalidFileType),
      })
      setCoverImage(imageFile)
    } else {
      if (!/(png|jpe?g)/i.test(file.type)) {
        setCoverImageError(validationErrorInvalidFileType)

        return
      }

      if (file.size > maxFileSize) {
        setCoverImageError(validationErrorFileTooLarge)

        return
      }

      checkIfImageRenders({
        file,
        handleError: () => setCoverImageError(validationErrorInvalidFileType),
      })
      setCoverImage(file)
    }
  }

  return {
    coverImage,
    coverImageError,
    setCoverImageError,
    handleCoverImageChange,
  }
}
